(() => {
  'use strict';
  const storageKey = 'mst-vehicle-v1';
  const readVehicle = () => { try { return JSON.parse(localStorage.getItem(storageKey)); } catch { return null; } };
  const json = (element, selector) => { try { return JSON.parse(element.querySelector(selector)?.textContent || 'null'); } catch { return null; } };
  class VehicleFinder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.vehicles = (json(this, '[data-vehicles]') || []).filter(v => v.brand && v.model && v.generation && MSTCommerce.safeDestination(v.url, location.origin));
      if (!this.vehicles.length) {
        const products = json(this, '[data-catalogue-products]') || [];
        this.vehicles = (json(this, '[data-catalogue-map]') || []).flatMap(entry => {
          const product = products.find(p => p.id === entry.product);
          const prefix = (this.dataset.rootUrl || '').replace(/\/$/, '');
          const url = entry.url ? prefix + entry.url : product?.url;
          return url && MSTCommerce.safeDestination(url, location.origin) ? [{ ...entry, url }] : [];
        });
        this.querySelector('[data-generation]').previousElementSibling.textContent = this.dataset.catalogueLabel;
      }
      if (!this.vehicles.length) return;
      const form = this.querySelector('form');
      const [brand, model, generation] = ['brand', 'model', 'generation'].map(key => this.querySelector(`[data-${key}]`));
      const fill = (select, entries) => {
        const placeholder = select.options[0].text;
        select.replaceChildren(new Option(placeholder, ''));
        entries.forEach(([label, value]) => select.add(new Option(label, value)));
        select.disabled = !entries.length;
      };
      const brands = [...new Set(this.vehicles.map(v => v.brand))].sort((a, b) => a.localeCompare(b));
      fill(brand, brands.map(v => [v, v]));
      const updateBrand = () => {
        const models = [...new Set(this.vehicles.filter(v => v.brand === brand.value).map(v => v.model))].sort((a,b) => a.localeCompare(b));
        fill(model, models.map(v => [v,v])); fill(generation, []);
      };
      const updateModel = () => fill(generation, this.vehicles.filter(v => v.brand === brand.value && v.model === model.value).map(v => [`${v.generation}${v.start ? ` · ${v.start}${v.end ? `–${v.end}` : ''}` : ''}`, v.id]));
      brand.addEventListener('change', updateBrand);
      model.addEventListener('change', updateModel);
      form.addEventListener('submit', event => {
        event.preventDefault();
        const vehicle = this.vehicles.find(v => v.id === generation.value && v.brand === brand.value && v.model === model.value);
        if (!vehicle || !form.reportValidity()) return;
        const destination = MSTCommerce.safeDestination(vehicle.url, location.origin);
        if (!destination) return;
        try { localStorage.setItem(storageKey, JSON.stringify(vehicle)); } catch { /* Navigation also works with storage disabled. */ }
        location.assign(destination);
      });
      const saved = readVehicle();
      if (saved && this.vehicles.some(v => v.id === saved.id)) {
        brand.value = saved.brand; updateBrand(); model.value = saved.model; updateModel(); generation.value = saved.id;
      }
      form.hidden = false;
      this.querySelector('.finder-fallback').hidden = true;
    }
  }
  class ProductPurchase extends HTMLElement {
    connectedCallback() {
      if (this.ready) { this.dockObserver?.observe(this.querySelector('[data-add-to-cart]')); return; }
      const data = json(this, '[data-product-json]');
      if (!data?.variants?.length) return;
      this.ready = true;
      const native = this.querySelector('[data-variant-select]');
      const options = [...this.querySelectorAll('[data-option-position]')];
      const button = this.querySelector('[data-add-to-cart]');
      const form = native.form;
      const dock = this.querySelector('[data-purchase-dock]');
      const dockButton = this.querySelector('[data-sticky-add-to-cart]');
      const purchaseButtons = [button, dockButton].filter(Boolean);
      const accessoryCards = [...this.querySelectorAll('[data-accessory-variants]')];
      let currentVariant, busy = false;
      const selectedAccessories = () => accessoryCards.filter(card => !card.hidden && card.querySelector('[data-accessory-check]')?.checked)
        .map(card => card.querySelector('[data-accessory-select]').selectedOptions[0]).filter(Boolean)
        .filter((option, index, all) => all.findIndex(other => other.value === option.value) === index);
      const updateSelection = () => {
        const selected = selectedAccessories();
        const total = currentVariant ? currentVariant.price + selected.reduce((sum, option) => sum + Number(option.dataset.price), 0) : null;
        const summary = this.querySelector('[data-selection-total]');
        if (summary) { summary.hidden = !selected.length; this.querySelector('[data-selection-price]').textContent = total === null ? '—' : money(total); }
        if (dock) {
          this.querySelector('[data-dock-price]').textContent = total === null ? '—' : money(total);
          const label = options.length ? options.map(select => select.selectedOptions[0]?.textContent.trim() || '').join(' · ') : 'Voir la référence';
          const selection = label + (selected.length ? ` + ${selected.length} complément${selected.length > 1 ? 's' : ''}` : '');
          this.querySelector('[data-dock-variant]').textContent = selection;
          this.querySelector('[data-dock-edit]').title = selection;
        }
      };
      const syncDock = () => {
        if (!dock) return;
        const rect = button.getBoundingClientRect();
        const mainVisible = rect.top >= 100 && rect.bottom <= window.innerHeight;
        dock.hidden = mainVisible && !dock.contains(document.activeElement);
      };
      if (dock) {
        if ('IntersectionObserver' in window) {
          this.dockObserver = new IntersectionObserver(syncDock, { rootMargin: '-100px 0px 0px 0px', threshold: [0, 1] });
          this.dockObserver.observe(button);
        }
        dock.addEventListener('focusout', () => requestAnimationFrame(syncDock));
        this.querySelector('[data-dock-edit]').addEventListener('click', event => {
          event.preventDefault();
          const target = options[0] || button;
          target.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
          target.focus({ preventScroll: true });
        });
      }
      const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'fr', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(cents / 100);
      const changeImage = link => {
        if (!link) return;
        const img = this.querySelector('.product-gallery-main img');
        if (!img) return;
        img.removeAttribute('srcset'); img.src = link.dataset.galleryImage; img.alt = link.dataset.imageAlt || '';
        if (link.dataset.imageWidth && link.dataset.imageHeight) { img.width = Number(link.dataset.imageWidth); img.height = Number(link.dataset.imageHeight); }
        this.querySelectorAll('[data-gallery-image]').forEach(a => a.removeAttribute('aria-current'));
        link.setAttribute('aria-current', 'true');
      };
      this.querySelectorAll('[data-gallery-image]').forEach(link => link.addEventListener('click', event => { event.preventDefault(); changeImage(link); }));
      const update = (writeURL = true) => {
        const variant = options.length ? MSTCommerce.variant(data.variants, options.map(select => select.value)) : data.variants[0];
        currentVariant = variant;
        native.value = variant ? String(variant.id) : '';
        purchaseButtons.forEach(control => {
          control.disabled = !variant?.available;
          control.replaceChildren(document.createTextNode(!variant ? this.dataset.unavailableLabel : variant.available ? this.dataset.availableLabel : this.dataset.soldOutLabel));
        });
        this.querySelector('[data-product-stock]').textContent = !variant ? this.dataset.unavailableLabel : variant.available ? this.dataset.stockLabel : this.dataset.soldOutLabel;
        const price = this.querySelector('[data-product-price]');
        price.replaceChildren();
        if (variant) {
          const current = document.createElement('span'); current.className = 'price'; current.textContent = money(variant.price); price.append(current);
          if (variant.compare_at_price > variant.price) { const old = document.createElement('s'); old.className = 'price-compare'; old.textContent = money(variant.compare_at_price); price.append(' ', old); }
          const sku = this.querySelector('[data-product-sku]'); sku.textContent = variant.sku || ''; sku.parentElement.hidden = !variant.sku;
          const imageId = variant.featured_image?.id || this.dataset.defaultMediaId;
          changeImage(this.querySelector(`[data-media-id="${imageId}"]`));
          const caption = this.querySelector('[data-gallery-caption]');
          if (caption) caption.hidden = Boolean(variant.featured_image);
        }
        const vehicle = readVehicle();
        const matches = MSTCommerce.fitment(data.rules || [], vehicle?.id, variant);
        const status = this.querySelector('[data-fitment-status]');
        status.textContent = vehicle && vehicle.approved !== false ? `${vehicle.brand} ${vehicle.model} ${vehicle.generation} — ${matches.length ? this.dataset.matchLabel : this.dataset.mismatchLabel}` : this.dataset.unknownLabel;
        matches.forEach(rule => { if (rule.conditions) { const note = document.createElement('p'); note.textContent = rule.conditions; status.append(note); } });
        const property = this.querySelector('[data-vehicle-property]');
        property.disabled = !matches.length; property.value = matches.length ? `${vehicle.brand} ${vehicle.model} ${vehicle.generation}` : '';
        accessoryCards.forEach(card => {
          const ids = (card.dataset.accessoryVariants || '').split(',').filter(Boolean);
          const requiredVehicle = card.dataset.accessoryVehicle;
          // A rule restricted to a vehicle also works on a direct product landing when
          // the selected variant has an approved fitment for that vehicle.
          const vehicleMatches = !requiredVehicle || (vehicle?.approved !== false && vehicle?.id === requiredVehicle)
            || MSTCommerce.fitment(data.rules || [], requiredVehicle, variant).length > 0;
          card.hidden = !variant?.available || (ids.length > 0 && !ids.includes(String(variant.id))) || !vehicleMatches;
          const check = card.querySelector('[data-accessory-check]');
          const select = card.querySelector('[data-accessory-select]');
          if (check && select) {
            check.disabled = card.hidden;
            select.disabled = card.hidden;
            if (card.hidden) check.checked = false;
            card.querySelector('[data-accessory-price]').textContent = '+ ' + money(Number(select.selectedOptions[0]?.dataset.price || 0));
          }
        });
        const accessories = this.querySelector('[data-accessories]');
        if (accessories) accessories.hidden = !accessoryCards.some(card => !card.hidden);
        updateSelection();
        if (writeURL) { const url = new URL(location.href); if (variant) url.searchParams.set('variant', variant.id); else url.searchParams.delete('variant'); history.replaceState({}, '', url); }
      };
      const initial = data.variants.find(v => String(v.id) === native.value) || data.variants[0];
      options.forEach((select, i) => { select.value = initial.options[i]; select.addEventListener('change', () => update()); });
      native.hidden = true;
      this.querySelector('[data-product-options]').hidden = false;
      accessoryCards.forEach(card => card.addEventListener('change', () => {
        const select = card.querySelector('[data-accessory-select]');
        card.querySelector('[data-accessory-price]').textContent = '+ ' + money(Number(select.selectedOptions[0]?.dataset.price || 0));
        updateSelection();
      }));
      form.addEventListener('submit', async event => {
        if (event.defaultPrevented) return;
        if (busy || button.disabled || !native.value) { event.preventDefault(); return; }
        const selected = selectedAccessories();
        // The ordinary capote purchase keeps Shopify's native, no-JS form submission.
        if (!selected.length) return;
        event.preventDefault();
        const payload = new FormData();
        for (const [name, value] of new FormData(form)) {
          if (['id', 'quantity', 'selling_plan'].includes(name)) payload.append(`items[0][${name}]`, value);
          else if (name.startsWith('properties[')) payload.append(name.replace('properties[', 'items[0][properties]['), value);
        }
        selected.forEach((option, index) => {
          payload.append(`items[${index + 1}][id]`, option.value);
          payload.append(`items[${index + 1}][quantity]`, '1');
        });
        const error = this.querySelector('[data-purchase-error]');
        error.hidden = true;
        busy = true;
        form.setAttribute('aria-busy', 'true');
        const controls = [...new Set([...form.querySelectorAll('input, select, button'), ...purchaseButtons])];
        const states = controls.map(control => control.disabled);
        controls.forEach(control => { control.disabled = true; });
        purchaseButtons.forEach(control => { control.textContent = 'Ajout en cours…'; });
        try {
          const response = await fetch(this.dataset.cartAddUrl, { method: 'POST', headers: { Accept: 'application/json' }, body: payload });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : 'Votre sélection n’a pas pu être ajoutée.');
          location.assign(this.dataset.cartUrl);
        } catch (failure) {
          error.querySelector('[data-purchase-error-text]').textContent = `${failure.message === 'Failed to fetch' ? 'La connexion a été interrompue.' : failure.message} Vérifiez votre panier avant de réessayer.`;
          error.hidden = false;
          error.scrollIntoView({ block: 'center' }); error.focus({ preventScroll: true });
        } finally {
          controls.forEach((control, index) => { control.disabled = states[index]; });
          busy = false; form.removeAttribute('aria-busy'); update(false);
        }
      });
      update(false);
      syncDock();
    }
    disconnectedCallback() { this.dockObserver?.disconnect(); }
  }
  customElements.define('vehicle-finder', VehicleFinder);
  customElements.define('product-purchase', ProductPurchase);
  function init(root = document) {
    root.querySelectorAll('[data-catalogue-brands]').forEach(details => {
      if (details.dataset.initialized) return;
      details.dataset.initialized = 'true';
      if (matchMedia('(max-width: 700px)').matches) details.open = false;
    });
    root.querySelectorAll('[data-auto-submit]').forEach(select => {
      if (select.dataset.initialized) return;
      select.dataset.initialized = 'true';
      select.addEventListener('change', () => select.form.requestSubmit());
    });
    root.querySelectorAll('[data-hero-video]').forEach(hero => {
      if (hero.dataset.initialized) return;
      hero.dataset.initialized = 'true';
      const video = hero.querySelector('video');
      if (!video || matchMedia('(prefers-reduced-motion: reduce)').matches || navigator.connection?.saveData || document.hidden) return;
      const header = document.querySelector('.site-header');
      let ended = false;
      const stop = () => { ended = true; video.pause(); video.classList.remove('is-playing'); header?.classList.remove('is-dark'); };
      video.addEventListener('playing', () => { if (ended) return stop(); video.classList.add('is-playing'); });
      video.addEventListener('timeupdate', () => header?.classList.toggle('is-dark', !ended && video.currentTime < 1.55 && !header?.classList.contains('is-scrolled')));
      video.addEventListener('ended', stop, { once: true }); video.addEventListener('error', stop, { once: true });
      hero.querySelector('vehicle-finder')?.addEventListener('focusin', stop, { once: true });
      video.src = video.dataset.src; video.muted = true; video.play().catch(stop);
      setTimeout(stop, 10000);
    });
    const header = document.querySelector('.site-header');
    if (header && !header.dataset.scrollReady) {
      header.dataset.scrollReady = 'true';
      let pending = false;
      const updateHeader = () => {
        const scrolled = window.scrollY > 32;
        header.classList.toggle('is-scrolled', scrolled);
        if (scrolled) header.classList.remove('is-dark');
        pending = false;
      };
      window.addEventListener('scroll', () => { if (!pending) { pending = true; requestAnimationFrame(updateHeader); } }, { passive: true });
      updateHeader();
    }
    const vehicle = readVehicle();
    const field = root.querySelector('[data-contact-vehicle]');
    if (field && vehicle && !field.value) field.value = vehicle.approved === false ? vehicle.generation : `${vehicle.brand} ${vehicle.model} ${vehicle.generation}`;
    const subject = new URLSearchParams(location.search).get('subject');
    const message = root.querySelector('[name="contact[body]"]');
    if (message && subject && !message.value) message.value = `${subject}\n\n`;
  }
  document.addEventListener('keydown', event => { if (event.key === 'Escape') document.querySelectorAll('.nav-menu[open],.mobile-menu[open]').forEach(el => { el.open = false; el.querySelector('summary').focus(); }); });
  document.addEventListener('shopify:section:load', event => init(event.target));
  init();
})();
